/* @flow */

import type { Component } from 'react';
import { createRefetchContainer, graphql } from 'react-relay';

// Components
import Folder from '../Folder';

// Helpers
import get from 'lodash.get';

// Types
import type { RelayRefetchProp } from 'react-relay';
import type { AddToFolderWrapperProps } from '../AddToFolderModalWrapper';
import type { TrackingHooksType } from '../../types';

type ModalComponentType = Class<Component<AddToFolderWrapperProps>>;

type FolderWrapperProps = {
    additionalIconClass?: string,
    allowSavingMultipleItems: boolean,
    closeModal?: () => void,
    currentPortfolioId?: string,
    disabledButton: boolean,
    environment: any,
    getModalComponent: () => Promise<ModalComponentType>,
    iconPosition: string,
    iconText?: string,
    loadPortfolioItemData: boolean,
    modalHeaderText: string,
    onAddItemError?: (e: ?Error) => void,
    onAddItemSuccess?: (?{ portfolioName?: string }) => void,
    onAddNoteError?: (e: ?Error) => void,
    onAddNoteSuccess?: () => void,
    onCreateFolderError?: (e: ?Error) => void,
    onCreateFolderSuccess?: () => void,
    onFolderClick?: (primaryItemPk?: string) => void,
    onFolderRemove?: () => void,
    onFolderSave?: () => void,
    onNewFolderSave?: () => void,
    onRemoveItemError?: (e: ?Error) => void,
    onRemoveItemSuccess?: () => void,
    primaryItemPk?: string,
    relay: RelayRefetchProp,
    shouldFillInFolder: boolean,
    selectedItemIds?: Array<string>,
    selectedItemImages?: Array<string>,
    selectedItemTitles?: Array<string>,
    singlePortfolioItem: boolean,
    trackingHooks: TrackingHooksType,
    type: 'outline' | 'button',
    userId?: string,
    userIds?: Array<string>,
    viewer?: {
        folderItemMatches?: Array<{
            item: { serviceId: string },
        }>,
    },
};

export function FolderWrapper(props: FolderWrapperProps) {
    const { primaryItemPk, viewer } = props;

    function doRefetch() {
        return new Promise(resolve => {
            props.relay.refetch(
                fragmentVariables => fragmentVariables,
                null,
                () => resolve()
            );
        });
    }

    const folderPortfolioItems = (get(viewer, 'folderItemMatches') || []).filter(
        folderItemMatch => {
            return primaryItemPk === get(folderItemMatch, `item.serviceId`);
        }
    );

    return (
        <Folder
            {...props}
            doRefetch={doRefetch}
            itemId={primaryItemPk}
            selectedItemIds={props.selectedItemIds}
            folderPortfolioItems={folderPortfolioItems}
        />
    );
}

export default createRefetchContainer(
    FolderWrapper,
    {
        viewer: graphql`
            fragment FolderWrapper_viewer on Viewer
            @argumentDefinitions(
                selectedItemIds: { type: "[String]", defaultValue: [] }
                shouldFillInFolder: { type: "Boolean", defaultValue: false }
                userIds: { type: "[String]", defaultValue: [] }
            ) {
                __typename
                folderItemMatches: portfolioItemMatches(
                    itemIds: $selectedItemIds
                    userIds: $userIds
                    portfolioTypes: [FOLDER]
                ) @include(if: $shouldFillInFolder) {
                    ...Folder_folderPortfolioItems
                    item {
                        serviceId
                    }
                }
            }
        `,
    },
    graphql`
        query FolderWrapperRefetchQuery(
            $selectedItemIds: [String]
            $shouldFillInFolder: Boolean!
            $userIds: [String]!
        ) {
            viewer {
                ...FolderWrapper_viewer
                @arguments(
                    selectedItemIds: $selectedItemIds
                    shouldFillInFolder: $shouldFillInFolder
                    userIds: $userIds
                )
            }
        }
    `
);
